import { Box, Center, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { RanaHeading, RanaModal } from "@nens/ranalib";

import LoadingIndicatorVisual from "../components/LoadingIndicatorVisual";
import { getThreediModel } from "../state/simulation";

import styles from "./LoadingScreen.module.css";

// from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/random
const getRandomInt = (max: number) => {
  return Math.floor(Math.random() * Math.floor(max));
};

const allTipsAndTricks = [
  <FormattedMessage
    id="loadingScreen.tip_1"
    defaultMessage={`
     A live simulation has to be paused before adding an event or editing a control structure.
    `}
  />,
  <FormattedMessage
    id="loadingScreen.tip_2"
    defaultMessage={`
    A simulation can be started from anywhere (for instance the Rana Desktop Client) and followed via Rana Live.
    `}
  />,
  <FormattedMessage
    id="loadingScreen.tip_3"
    defaultMessage={`
    When storing the simulation it is possible to store the Rana Live session as a simulation template. With this template you can re-run the exact same simulation again!
    `}
  />,
  <FormattedMessage
    id="loadingScreen.tip_4"
    defaultMessage={`
Rana handles large rasters like a boss! The maximum size of the Digital Elevation Model is 5 billion pixels (this includes NoData pixels).
    `}
  />,
  <FormattedMessage
    id="loadingScreen.tip_5"
    defaultMessage={`
   Go easy on those grid refinements! By keeping your model grid size below 100,000 nodes, no issues are to be expected and the model will generally run smoothly.
    `}
  />,
  <FormattedMessage
    id="loadingScreen.tip_6"
    defaultMessage={`
   Rana has a module that can be used for water quality calculations! You can introduce concentrations of substances to the simulation, and compute how these substances spread through the water system.
    `}
  />,
  <FormattedMessage
    id="loadingScreen.tip_7"
    defaultMessage={`
 Structure control actions during the simulation are saved in a seperate NetCDF file and can be analysed in the Rana Desktop Client.
    `}
  />,
  <>
    <b>
      <FormattedMessage
        id="loadingScreen.tip_12_cant_find_model"
        defaultMessage={`Can't find the model you are looking for?`}
      />
    </b>
    <br />
    <FormattedMessage
      id="loadingScreen.tip_12_please_check_following"
      defaultMessage={`Please check the following:`}
    />

    <ol className={styles.OrderedListInTip}>
      <li>
        <FormattedMessage
          id="loadingScreen.tip_12_have_access_to_organisation"
          defaultMessage={`If you have access to the organisation to which the model belongs`}
        />
      </li>
      <li>
        <FormattedMessage
          id="loadingScreen.tip_12_is_model_available_in_apiv3"
          defaultMessage={`If the model is available on API v3 at: `}
        />
        <a href="https://docs.ranawaterintelligence.com/j_api.html" target="blank">
          https://docs.ranawaterintelligence.com/j_api.html
        </a>
      </li>
    </ol>
    <FormattedMessage
      id="loadingScreen.tip_12_if_needed_contact_support"
      defaultMessage={`Please contact the service desk if you need assistance.`}
    />
  </>,
];

const LoadingScreen: React.FC = () => {
  const threedimodel = useSelector(getThreediModel);

  const currentTrickNr = getRandomInt(allTipsAndTricks.length);
  const currentTrick = allTipsAndTricks[currentTrickNr];

  return (
    <RanaModal
      isOpen={true}
      width="800px"
      overflow="hidden"
      bgGradient="linear(to-r, #fff 0%, #b7d1e4 100%)"
      bodyPx={2}
    >
      <Flex pb={12} px={12} gap="74px" overflow="hidden">
        <Box w="50%">
          <RanaHeading type="h2" mb={2}>
            <FormattedMessage id="loadingScreen.loading" defaultMessage="Simulations is loading" />
          </RanaHeading>
          <Text fontSize="18px">
            <FormattedMessage
              id="select_model.table_header_simulation"
              defaultMessage="Simulation"
            />
            : <Text as="strong"> {threedimodel && threedimodel.name}</Text>
          </Text>
          <RanaHeading mt={4} mb={2} type="h3">
            <FormattedMessage id="loadingScreen.info_title" defaultMessage="Tips and Tricks" />
          </RanaHeading>
          <Text fontSize="18px">{currentTrick}</Text>
        </Box>

        <Center w="50%" alignItems="center">
          <LoadingIndicatorVisual />
        </Center>
      </Flex>
    </RanaModal>
  );
};

export default LoadingScreen;
